// src/components/atoms/ConnectedFamilyCard/ConnectedFamilyCard.tsx
import React from "react";
import { User } from "src/types/User";
import daughterPng from "../../../styles/assets/img/icon_red.png";
import husbandPng from "../../../styles/assets/img/icon_blue.png";

type Props = {
  members: User[];
  count: number; // 헤더 배지 숫자
  px: (n: number) => number;

  // 헤더 오른쪽 > (필요한 페이지에서만 사용)
  showChevron?: boolean;
  onChevronClick?: () => void;

  // 하단 “초대하기” 버튼
  showAddButton?: boolean; // InvitePage에서는 false
  onAdd?: () => void;
  addIconSrc?: any; // + 아이콘 이미지 (svg/png 등)
};

export const ConnectedFamilyCard: React.FC<Props> = ({
  members,
  count,
  px,
  showChevron = false,
  onChevronClick,
  showAddButton = true,
  onAdd,
  addIconSrc,
}) => {
  const avatarSize = px(44);

  const imgRender = (gender: string) => {
    if (gender === "M") return husbandPng;
    return daughterPng;
  };

  return (
    <section
      style={{
        background: "#FFFFFF",
        borderRadius: px(16),
        boxShadow: "0px 4px 11px rgba(0,0,0,0.04)",
        outline: `1px solid #E5E5E5`,
        outlineOffset: "-1px",
        padding: px(16),
      }}
    >
      {/* 헤더: '연결된 가족' + 카운트 + (옵션) > */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: px(12),
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: px(8) }}>
          <div style={{ color: "#404040", fontSize: px(16), fontWeight: 500 }}>
            연결된 가족
          </div>
          <div
            style={{
              height: px(20),
              padding: `${px(2)}px ${px(6)}px`,
              background: "#F35143",
              color: "#fff",
              borderRadius: 111,
              fontSize: px(13),
              fontWeight: 700,
              lineHeight: `${px(16)}px`,
            }}
          >
            {count}명
          </div>
        </div>

        {showChevron ? (
          <button
            aria-label="chevron"
            onClick={onChevronClick}
            style={{
              width: px(24),
              height: px(24),
              border: "none",
              background: "transparent",
              cursor: "pointer",
              display: "grid",
              placeItems: "center",
              padding: 0,
            }}
          >
            <svg width={px(18)} height={px(18)} viewBox="0 0 24 24">
              <path
                d="M9 18l6-6-6-6"
                stroke="#404040"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </button>
        ) : (
          <div style={{ width: px(24), height: px(24) }} />
        )}
      </div>

      {/* 그리드 (3열 고정) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: px(16),
        }}
      >
        {members.map((m) => (
          <div
            key={m.oid}
            style={{ display: "grid", justifyItems: "center", gap: px(8) }}
          >
            <div
              style={{
                padding: px(10),
                background: "#FAFAFA",
                borderRadius: 9999,
                outline: `1.5px solid #F5F5F5`,
                outlineOffset: -1.5,
                display: "inline-flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={
                  m.profileImage?.startsWith("http")
                    ? m.profileImage
                    : m.profileImage !== "" && m.profileImage !== null
                    ? process.env.REACT_APP_API_FILE! + m.profileImage
                    : imgRender(m.gender || "")
                }
                alt={`${m.userName} 아바타`}
                loading="lazy"
                decoding="async"
                style={{
                  width: avatarSize,
                  height: avatarSize,
                  borderRadius: 9999,
                  objectFit: "cover",
                  display: "block",
                  background: "#F3F4F6",
                  boxShadow: "0 2px 8px rgba(0,0,0,.06)",
                }}
              />
            </div>
            <div
              style={{
                textAlign: "center",
                color: "#A3A3A3",
                fontSize: px(14),
                fontWeight: 500,
                lineHeight: `${px(20)}px`,
              }}
            >
              {m.userName}
            </div>
          </div>
        ))}

        {/* 초대하기 버튼 (옵션) */}
        {showAddButton && (
          <button
            onClick={onAdd}
            aria-label="가족 초대하기"
            style={{
              border: "none",
              background: "transparent",
              padding: 0,
              cursor: "pointer",
            }}
          >
            <div
              style={{ display: "grid", justifyItems: "center", gap: px(8) }}
            >
              <div
                style={{
                  width: px(64),
                  height: px(64),
                  padding: 16.64, // 피그마 고정값
                  borderRadius: 32,
                  border: "2px dashed #E5E5E5",
                  background: "#FFFFFF",
                  display: "inline-flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 12.8,
                }}
              >
                {addIconSrc ? (
                  <img
                    src={addIconSrc}
                    alt=""
                    width={40.96}
                    height={40.96}
                    loading="lazy"
                    decoding="async"
                    style={{ display: "block" }}
                  />
                ) : null}
              </div>

              <div
                style={{
                  textAlign: "center",
                  color: "#A3A3A3",
                  fontSize: px(14),
                  fontWeight: 500,
                  lineHeight: `${px(20)}px`,
                }}
              >
                초대하기
              </div>
            </div>
          </button>
        )}
      </div>
    </section>
  );
};
